import * as React from 'react';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../../AnalysisList';
import { adjustTextColor } from '../../../Palette/adjustTextColor';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import NotEnoughData from '../../NotEnoughData';
import NotEnoughTagGroups from '../../NotEnoughTagGroups';

import Chart, { Data, GroupAnalysis, ItemAnalysis } from './Chart';

export interface TagsAndKeywordsProps extends ChartProps {}

const TagsAndKeywords: React.FC<TagsAndKeywordsProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showSerpOnly: true,
          showTagGroupId: true,
          showLimitItems: true,
        },
      })),
    []
  );

  // Fetch data
  const iIByTagsQuery = projectsApiService.queries.analysis.useGetTagsAndKeywords({
    projectId: project.id,
    tagGroupId: analysisFilterState?.tagGroupId,
    serpOnly: analysisFilterState?.serpOnly,
    limitItems: analysisFilterState?.limitItems,
  });

  const iIByTagsLoading = iIByTagsQuery?.query?.loading;
  const iIByTagsResult = iIByTagsQuery?.result;

  const transformedData = React.useMemo(
    (): Data => ({
      projectSearchVolume: iIByTagsResult?.projectSearchVolume ?? 0,
      itemAnalysis:
        iIByTagsResult?.tagAnalyses.map(
          (tga): GroupAnalysis => ({
            label: tga.tagName,
            id: tga.tagId,
            moreItemsCount: tga.keywordCount - tga.keywordAnalyses.length,
            searchVolume: tga.searchVolume,
            items: tga.keywordAnalyses.map(
              (kw): ItemAnalysis => ({
                id: kw.keywordId,
                label: kw.keywordName,
                searchVolume: kw.searchVolume,
              })
            ),
          })
        ) ?? [],
    }),
    [iIByTagsResult, analysisFilterState?.limitItems]
  );

  const textColor = adjustTextColor(palette, 0.85, 1);

  // Render
  if (didQueryTimeout(iIByTagsQuery?.query)) {
    return <DataTimedOut />;
  }

  if (iIByTagsLoading) {
    return <AnalysisChartLoading />;
  }

  if (!(analysisFilterState?.tagGroupId > 0)) {
    return <NotEnoughTagGroups />;
  }

  if (!iIByTagsResult.tagAnalyses.length) {
    return <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <Chart
        data={transformedData}
        progressColor={palette.secondaryScale[1]}
        backgroundColor={palette.tertiaryScale[1]}
        language={language}
        labelColor={textColor}
      />
    </div>
  );
};

export default TagsAndKeywords;
